import React from 'react';
import styled from 'styled-components';
import Section from './Section';
import Container from './Container';

const AboutContent = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  position: relative;
  margin-bottom: ${props => props.theme.spacing.xl};
  text-align: center;
  font-size: 2.5rem;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -${props => props.theme.spacing.sm};
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: ${props => props.theme.colors.primary};
  }
`;

const SubsectionTitle = styled.h3`
  position: relative;
  margin-top: ${props => props.theme.spacing.xxl};
  margin-bottom: ${props => props.theme.spacing.xl};
  text-align: center;
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  
  &:after {
    content: '';
    position: absolute;
    bottom: -${props => props.theme.spacing.sm};
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background-color: ${props => props.theme.colors.secondary};
  }
`;

const ProcessSteps = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xl};
`;

const StepCard = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.lg};
  background: ${props => props.theme.colors.light};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.lg};
  box-shadow: ${props => props.theme.shadows.md};
  transition: ${props => props.theme.transitions.medium};
  
  &:hover {
    transform: translateX(10px);
    box-shadow: ${props => props.theme.shadows.lg};
  }
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    flex-direction: column;
    gap: ${props => props.theme.spacing.md};
  }
`;

const StepNumber = styled.div`
  flex-shrink: 0;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  font-weight: ${props => props.theme.fontWeights.bold};
  box-shadow: ${props => props.theme.shadows.md};
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    margin: 0 auto;
  }
`;

const StepContent = styled.div`
  flex: 1;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    text-align: center;
  }
`;

const StepText = styled.p`
  margin: 0;
  line-height: 1.7;
  font-size: ${props => props.theme.fontSizes.lg};
  color: ${props => props.theme.colors.dark};
`;

const About: React.FC = () => {
  return (
    <Section id="about" backgroundColor="background">
      <Container>
        <AboutContent>
          <SectionTitle>How We Help</SectionTitle>
          
          <ProcessSteps>
            <StepCard>
              <StepNumber>1</StepNumber>
              <StepContent>
                <StepText>
                  A family in need reaches out to Project Assist through the community or our website for an interview
                </StepText>
              </StepContent>
            </StepCard>
            
            <StepCard>
              <StepNumber>2</StepNumber>
              <StepContent>
                <StepText>
                  We create a path based on their situation and skill set on how to become self sustainable within a reasonable amount of time.
                </StepText>
              </StepContent>
            </StepCard>
            
            <StepCard>
              <StepNumber>3</StepNumber>
              <StepContent>
                <StepText>
                  We devise a plan that can include job searching, affordable housing, resume help, budgeting and financial advice, and anything necessary to put a family in a strong position to support themselves independently
                </StepText>
              </StepContent>
            </StepCard>
            
            <StepCard>
              <StepNumber>4</StepNumber>
              <StepContent>
                <StepText>
                  Families that show motivation and willingness to work hard receive our guidance through this entire process towards self sustainability.
                </StepText>
              </StepContent>
            </StepCard>
          </ProcessSteps>

          <SubsectionTitle>Project Assist's Family Sponsorship Program</SubsectionTitle>
          
          <ProcessSteps>
            <StepCard>
              <StepNumber>1</StepNumber>
              <StepContent>
                <StepText>
                  Families who have been vetted through government organizations and are eligible to come to the United States through family sponsorship programs contact Project Assist through the community or our website
                </StepText>
              </StepContent>
            </StepCard>
            
            <StepCard>
              <StepNumber>2</StepNumber>
              <StepContent>
                <StepText>
                  The refugee family is interviewed in a phone call
                </StepText>
              </StepContent>
            </StepCard>
            
            <StepCard>
              <StepNumber>3</StepNumber>
              <StepContent>
                <StepText>
                  Project Assist connects with the Sponsorship program and sponsors that family to come to the United States
                </StepText>
              </StepContent>
            </StepCard>
            
            <StepCard>
              <StepNumber>4</StepNumber>
              <StepContent>
                <StepText>
                  Prior to arrival Project Assist creates a plan with the family for housing as well as a path for them to be financially independent within a few months
                </StepText>
              </StepContent>
            </StepCard>
            
            <StepCard>
              <StepNumber>5</StepNumber>
              <StepContent>
                <StepText>
                  In this process we assist with housing, job search, immigration, mental health references, drivers license, school registration, eligible government programs, personal advice, friendship, and anything necessary to give a family the tools to be independent in a reasonable amount of time
                </StepText>
              </StepContent>
            </StepCard>
          </ProcessSteps>
        </AboutContent>
      </Container>
    </Section>
  );
};

export default About; 